import { useState } from 'react';
import PropTypes from 'prop-types';
import {
	IconButton,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

/**
 * ActionMenu component - Displays action buttons and dropdown menu for AMFI items
 */
const ActionMenu = ({
	item,
	type,
	onEdit,
	onDelete,
	onReclassify,
	onToggleKey,
}) => {
	const [anchorEl, setAnchorEl] = useState(null);
	const open = Boolean(anchorEl);

	const handleMenuOpen = (event) => {
		setAnchorEl(event.currentTarget);
	};

	const handleMenuClose = () => {
		setAnchorEl(null);
	};

	return (
		<div style={{ display: 'flex', alignItems: 'center' }}>
			{/* Star toggle button */}
			<Tooltip
				title={item.is_key ? 'Remove from key items' : 'Mark as key item'}
			>
				<IconButton
					size='small'
					onClick={() => onToggleKey(item)}
					style={{ marginRight: '4px' }}
				>
					{item.is_key ? <StarIcon color='primary' /> : <StarBorderIcon />}
				</IconButton>
			</Tooltip>

			{/* Menu button */}
			<IconButton
				size='small'
				aria-label='more'
				id='more-button'
				aria-controls={open ? 'more-menu' : undefined}
				aria-expanded={open ? 'true' : undefined}
				aria-haspopup='true'
				onClick={handleMenuOpen}
			>
				<MoreVertIcon fontSize='small' />
			</IconButton>

			{/* Dropdown menu */}
			<Menu
				id='more-menu'
				anchorEl={anchorEl}
				open={open}
				onClose={handleMenuClose}
				MenuListProps={{
					'aria-labelledby': 'more-button',
				}}
			>
				<MenuItem
					onClick={() => {
						handleMenuClose();
						onEdit(item);
					}}
				>
					<ListItemIcon>
						<EditIcon fontSize='small' />
					</ListItemIcon>
					<ListItemText>Edit</ListItemText>
				</MenuItem>
				<MenuItem
					onClick={() => {
						handleMenuClose();
						onDelete(item.id);
					}}
				>
					<ListItemIcon>
						<DeleteIcon fontSize='small' />
					</ListItemIcon>
					<ListItemText>Delete</ListItemText>
				</MenuItem>
				{/* Reclassify submenu section */}
				<MenuItem disabled sx={{ opacity: 0.7 }}>
					<ListItemText>Move to:</ListItemText>
				</MenuItem>
				{['attribute', 'metric', 'fact', 'insight']
					.filter((targetType) => targetType !== type)
					.map((targetType) => (
						<MenuItem
							key={targetType}
							onClick={() => {
								handleMenuClose();
								onReclassify(item, targetType);
							}}
							sx={{ pl: 3 }}
						>
							<ListItemText>
								{targetType.charAt(0).toUpperCase() + targetType.slice(1)}
							</ListItemText>
						</MenuItem>
					))}
			</Menu>
		</div>
	);
};

ActionMenu.propTypes = {
	item: PropTypes.shape({
		id: PropTypes.string.isRequired,
		is_key: PropTypes.bool,
	}).isRequired,
	type: PropTypes.oneOf(['attribute', 'metric', 'fact', 'insight']).isRequired,
	onEdit: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onReclassify: PropTypes.func.isRequired,
	onToggleKey: PropTypes.func.isRequired,
};

export default ActionMenu;
